import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { getSupabaseClient } from '../lib/supabase';

function identifyUser(
  posthog: ReturnType<typeof usePostHog>,
  userId: string,
  email: string | undefined,
  profile: { full_name: string } | null,
) {
  try {
    posthog.identify(userId, {
      email,
      name: profile?.full_name || undefined,
      role: 'guide',
      app: 'guide',
    });
  } catch {
    // PostHog identify failed — not critical, swallow silently
  }
}

export function PostHogIdentify() {
  const posthog = usePostHog();

  useEffect(() => {
    if (!posthog) return;

    let supabase: ReturnType<typeof getSupabaseClient>;
    try {
      supabase = getSupabaseClient();
    } catch {
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // IMPORTANT: this callback is awaited by _notifyAllSubscribers while
        // the auth lock is held.  Any Supabase query (e.g. .from().select())
        // calls getSession() internally, which tries to re-acquire the same
        // lock → deadlock.  Deferring via setTimeout breaks out of the lock
        // context so the queries run after the lock is released.
        if (event === 'SIGNED_IN' && session?.user) {
          const user = session.user;
          setTimeout(async () => {
            try {
              const { data: profile } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('id', user.id)
                .single();

              identifyUser(posthog, user.id, user.email, profile);
            } catch {
              // Non-critical
            }
          }, 0);
        }

        if (event === 'SIGNED_OUT') {
          try { posthog.reset(); } catch { /* non-critical */ }
        }
      }
    );

    // Identify on mount if already signed in — use getSession (synchronous from cache)
    // instead of getUser (requires network + auth lock) to avoid deadlocking with
    // other components that also call getUser on mount (GCS-218)
    supabase.auth.getSession()
      .then(async ({ data: { session } }) => {
        if (!session?.user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', session.user.id)
          .single();

        identifyUser(posthog, session.user.id, session.user.email, profile);
      })
      .catch(() => {
        // Non-critical — swallow silently
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [posthog]);

  return null;
}
